import React, { useState, useEffect, useRef } from 'react';
import styles from './RoundNavigation.module.css';
import { RoundData } from '../utils/types';

interface RoundNavigationProps {
  currentRound: number;
  roundsData: RoundData[];
  onRoundChange: (round: number) => void;
  onRewindToRoundStart: (round: number) => void;
  hasSubscription: boolean;
  matchId: string;
}

const RoundNavigation: React.FC<RoundNavigationProps> = ({
  currentRound,
  roundsData,
  onRoundChange,
  onRewindToRoundStart,
  hasSubscription,
  matchId,
}) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [roundsPerPage, setRoundsPerPage] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // Add function to check if match is 30 days or older
  const isMatchOlderThan30Days = (matchId: string): boolean => {
    const matchDate = new Date(matchId.substring(0, 10));
    const currentDate = new Date();
    const diffTime = Math.abs(currentDate.getTime() - matchDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays >= 30;
  };

  // Calculate how many rounds fit in the container
  useEffect(() => {
    const calculateRoundsPerPage = () => {
      console.log('calculating rounds per page');
      if (!containerRef.current) return;
      
      const containerWidth = containerRef.current.clientWidth;
      const roundButtonWidth = 32; // Width of each round button
      const roundButtonGap = 5; // Gap between buttons
      const separatorWidth = 21; // Width of separator (1px + 10px margin on each side)
      
      // Calculate how many rounds fit in the container
      let roundsThatFit = 0;
      let currentWidth = 0;
      
      for (let i = 0; i < roundsData.length; i++) {
        currentWidth += roundButtonWidth + roundButtonGap;
        
        // Add separator width every 12 rounds
        if ((i + 1) % 12 === 0 && i !== roundsData.length - 1) {
          currentWidth += separatorWidth;
        }
        
        if (currentWidth > containerWidth) {
          break;
        }
        
        roundsThatFit++;
      }
      
      setRoundsPerPage(Math.min(roundsThatFit, 24));
    };

    calculateRoundsPerPage();
    window.addEventListener('resize', calculateRoundsPerPage);
    return () => window.removeEventListener('resize', calculateRoundsPerPage);
  }, [roundsData.length]);

  // Calculate total pages
  const totalPages = Math.ceil(roundsData.length / roundsPerPage);

  // Calculate which rounds to show on current page
  const startIndex = currentPage * roundsPerPage;
  const endIndex = Math.min(startIndex + roundsPerPage, roundsData.length);
  const visibleRounds = roundsData.slice(startIndex, endIndex);

  // Handle page navigation
  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(0, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(totalPages - 1, prev + 1));
  };

  return (
    <div className={styles.roundNavigation} ref={containerRef}>
      {currentPage > 0 && (
        <button
          className={styles.pageButton}
          onClick={handlePreviousPage}
          title="Previous page"
        >
          <i className="bi bi-arrow-left"></i>
        </button>
      )}
      <div className={styles.roundButtonsContainer}>
        {visibleRounds.map((roundData, index) => {
          const actualIndex = startIndex + index;
          const isRestricted = actualIndex + 1 > 12 && !hasSubscription && !isMatchOlderThan30Days(matchId);
          return (
            <React.Fragment key={actualIndex + 1}>
              <button
                onClick={() => {
                  if (!roundData.enabled) return;
                  if (currentRound === actualIndex + 1) {
                    onRewindToRoundStart(actualIndex + 1);
                  } else {
                    onRoundChange(actualIndex + 1);
                  }
                }}
                className={`${styles.roundButton} 
                  ${currentRound === actualIndex + 1 ? styles.active : ''}
                  ${!roundData.enabled ? styles.disabled : ''}
                  ${isRestricted ? styles.restricted : ''}`}
                disabled={!roundData.enabled}
                title={isRestricted ? "Round unavailable for this account" : undefined}
              >
                {isRestricted ? (
                  <i className="bi bi-lock-fill"></i>
                ) : (
                  actualIndex + 1
                )}
              </button>
              {(actualIndex + 1) % 12 === 0 && actualIndex !== roundsData.length - 1 && (
                <div className={styles.roundSeparator} />
              )}
            </React.Fragment>
          );
        })}
      </div>
      {currentPage < totalPages - 1 && (
        <button
          className={styles.pageButton}
          onClick={handleNextPage}
          title="Next page"
        >
          <i className="bi bi-arrow-right"></i>
        </button>
      )}
    </div>
  );
};

export default RoundNavigation;
